/**
 * One person — customer or advocate — and every case attached to them.
 *
 * ## Layout: three stacked panels, not a sidebar
 *
 * Details, then figures, then the cases. Each is full width and each answers one question, in the
 * order an admin actually asks them: *who is this and how do I reach them* → *how much have they
 * done with us* → *show me the work*. A narrow details column beside the table made the address
 * wrap to four lines while the table it sat next to was still scrolling sideways.
 *
 * ## Why one component for both roles
 *
 * The two pages answer the same questions about different roles. The only differences are which
 * endpoint to call, which contact fields exist, and whether the middle panel counts caseload or
 * spend. Two files would have been the same table twice, and the second copy is the one that stops
 * getting fixes.
 *
 * ## Why every field is copyable
 *
 * Nearly everything here gets pasted somewhere else — an email into a mail client, a mobile into
 * WhatsApp, an address onto a courier label, an id into a support thread. Selecting text out of a
 * cramped grid without picking up the label with it is the kind of small friction that makes people
 * retype things, and retyped addresses are how parcels go missing.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Copy, MapPin } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/apiClient";
import { contactEmail } from "@/lib/contact";
import { StatusPill } from "@/components/StatusPill";
import { AdminTable, CopyableId, DateTimeCell, SignInBadges } from "./lib";
import { fmtDate } from "@/lib/dates";
import { addressLines } from "@/lib/address";

const GRID = "grid-cols-[110px_minmax(150px,1fr)_120px_115px_110px_110px_110px]";
const COLUMNS = [
  { key: "txn", label: "Txn ID" },
  { key: "document", label: "Document" },
  { key: "created", label: "Created" },
  { key: "status", label: "Status" },
  { key: "payment", label: "Payment" },
  { key: "review", label: "Review" },
  { key: "delivery", label: "Delivery" },
];

/** Section shell — a titled panel, so the three read as siblings rather than one long page. */
const Panel = ({ title, children, testid }) => (
  <section className="rounded-2xl border border-white/10 bg-[#111623] overflow-hidden" data-testid={testid}>
    <div className="px-5 py-3 border-b border-white/5 text-[10px] tracking-[0.24em] uppercase text-[#6B7280]">
      {title}
    </div>
    <div className="p-5">{children}</div>
  </section>
);

/** The copy affordance on its own, for rows that supply their own label and layout. */
const CopyButton = ({ value, label, testid }) => {
  const copy = async () => {
    try { await navigator.clipboard.writeText(String(value)); toast.success(`${label} copied`); }
    catch { toast.error("Could not copy — clipboard unavailable"); }
  };
  return (
    <button onClick={copy} data-testid={testid} title={`Copy ${label}`} aria-label={`Copy ${label}`}
            className="text-[#6B7280] hover:text-[#CBA96C] transition-colors shrink-0 mt-0.5">
      <Copy className="w-3 h-3" />
    </button>
  );
};

/**
 * A labelled value with a copy button.
 *
 * Copies the **whole** value even when the display is truncated — an id shown as `08bccac2…` has to
 * paste as the full uuid or the button is a trap. A missing value renders the label with a dash and
 * no button, rather than a control that would copy an empty string.
 */
const CopyField = ({ label, value, display, mono, testid }) => (
  <div className="min-w-0">
    <div className="text-[10px] tracking-[0.18em] uppercase text-[#6B7280]">{label}</div>
    {value ? (
      <div className="flex items-start gap-1.5 mt-1">
        <div className={`text-sm text-white min-w-0 whitespace-pre-line break-words ${mono ? "font-mono text-xs" : ""}`}>
          {display || value}
        </div>
        <CopyButton value={value} label={label} testid={testid} />
      </div>
    ) : (
      <div className="text-sm text-[#4B5563] mt-1">—</div>
    )}
  </div>
);

/**
 * Every delivery address the customer has saved, one row each.
 *
 * A customer may hold up to five (`addresses` on the user, capped in routers/auth.py), each a
 * `{label, address}` pair — "Home", "Office", a parent's house. One field could only ever show one
 * of them, and admin arranging a courier needs to see which is which.
 *
 * `postal_address` is the **legacy** single field: the profile page has not written it since
 * addresses became a list, so it survives only on older accounts. It is shown as a final row when
 * there is no list at all, rather than being silently dropped — those customers still have a real
 * address and it is still where their documents go.
 */
const AddressList = ({ addresses, legacy }) => {
  const rows = (addresses || []).filter(a => a && (addressLines(a).length || a.label));
  if (!rows.length && !legacy) {
    return (
      <div>
        <div className="text-[10px] tracking-[0.18em] uppercase text-[#6B7280]">Delivery addresses</div>
        <div className="text-sm text-[#4B5563] mt-1">None saved — checkout will ask for one.</div>
      </div>
    );
  }
  return (
    <div className="sm:col-span-2 lg:col-span-3">
      <div className="text-[10px] tracking-[0.18em] uppercase text-[#6B7280]">
        Delivery addresses{rows.length ? ` (${rows.length}/5)` : ""}
      </div>
      <div className="mt-2 rounded-xl border border-white/10 divide-y divide-white/5" data-testid="person-addresses">
        {rows.map((a, i) => (
          <div key={i} className="flex items-start gap-3 px-4 py-3">
            <MapPin className="w-3.5 h-3.5 text-[#CBA96C] mt-0.5 shrink-0" strokeWidth={1.5} />
            <div className="min-w-0 flex-1">
              <div className="text-xs text-[#CBA96C]">{a.label || `Address ${i + 1}`}</div>
              {/* Composed from the structured fields, falling back to the old free-text block —
                  reading `a.address` alone printed an em dash for every address saved since the
                  fields were added. Printed as lines, which is how it goes on a courier label. */}
              <div className="text-sm text-white whitespace-pre-line break-words mt-0.5">{addressLines(a).join("\n") || "—"}</div>
            </div>
            <CopyButton value={`${a.label ? a.label + ": " : ""}${addressLines(a).join(", ")}`}
                        label={a.label || `Address ${i + 1}`} testid={`copy-address-${i}`} />
          </div>
        ))}
        {!rows.length && legacy && (
          <div className="flex items-start gap-3 px-4 py-3">
            <MapPin className="w-3.5 h-3.5 text-[#6B7280] mt-0.5 shrink-0" strokeWidth={1.5} />
            <div className="min-w-0 flex-1">
              <div className="text-xs text-[#6B7280]">Legacy address</div>
              <div className="text-sm text-white whitespace-pre-line break-words mt-0.5">{legacy}</div>
            </div>
            <CopyButton value={legacy} label="Address" testid="copy-address-legacy" />
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, testid }) => (
  <div className="rounded-xl border border-white/10 bg-[#0B111C] px-5 py-4" data-testid={testid}>
    <div className="text-[10px] tracking-[0.18em] uppercase text-[#6B7280]">{label}</div>
    <div className="font-serif-display text-2xl text-white mt-1">{value}</div>
  </div>
);

function PersonDetail({ role }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const isReviewer = role === "reviewer";
  const backTo = isReviewer ? "/admin/reviewers" : "/admin/users";

  useEffect(() => {
    setData(null);
    setError(null);
    api.get(`/admin/${isReviewer ? "reviewers" : "users"}/${id}`)
      .then(r => setData(r.data))
      // The id comes from the URL, so a stale link or a deleted account lands here. Saying which it
      // is beats a spinner that never resolves.
      .catch(e => setError(e?.response?.status === 404
        ? `That ${isReviewer ? "advocate" : "customer"} no longer exists.`
        : "Could not load this record."));
  }, [id, isReviewer]);

  const back = (
    <button onClick={() => nav(backTo)} data-testid="person-back"
            className="inline-flex items-center gap-1.5 text-sm text-[#A0AABF] hover:text-white transition-colors">
      <ArrowLeft className="w-4 h-4" /> Back to {isReviewer ? "Reviewers" : "Users"}
    </button>
  );

  if (error) {
    return <div className="space-y-4" data-testid="admin-person-page">{back}
      <div className="text-sm text-[#F27373]">{error}</div></div>;
  }
  if (!data) {
    return <div className="space-y-4" data-testid="admin-person-page">{back}
      <div className="text-sm text-[#6B7280]">Loading…</div></div>;
  }

  const u = data.user || {};
  const txns = data.transactions || [];
  // Spend counts what was actually collected, so an unpaid draft does not read as revenue.
  const paid = txns.filter(t => t.payment_status === "completed");
  const spend = paid.reduce((sum, t) => sum + Number(t.price || 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-person-page">
      {back}

      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <h1 className="font-serif-display text-3xl md:text-4xl text-white" data-testid="person-name">
          {u.name || "—"}
        </h1>
        <span className="text-[10px] tracking-[0.24em] uppercase text-[#CBA96C]">
          {isReviewer ? "Advocate" : "Customer"}
        </span>
      </div>

      {/* 1 — who they are */}
      <Panel title="Details" testid="person-details">
        <div className="grid gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
          <CopyField label="Name" value={u.name} testid="copy-person-name" />
          {/* The address we write to — typed in the profile, else the provider's. The sign-in
              address is shown separately only when it differs, or is absent: a customer whose
              Facebook account shared no email has none, and support should see that. */}
          <CopyField label="Email" value={contactEmail(u)} testid="copy-person-email" />
          {!isReviewer && contactEmail(u) !== (u.email || "") && (
            <CopyField label="Sign-in email" value={u.email} display={u.email || "none shared by provider"}
                       testid="copy-person-signin-email" />
          )}
          <CopyField label="Mobile" value={u.mobile} testid="copy-person-mobile" />
          {/* An advocate has a bar registration; a customer has somewhere to send the parcel. */}
          {isReviewer && <CopyField label="Bar Council ID" value={u.bar_council_id} testid="copy-person-bar" />}
          <CopyField label="Joined" value={u.created_at}
                     display={u.created_at ? fmtDate(u.created_at) : null} testid="copy-person-joined" />
          {/* Customers only. A customer holds no password, so the provider is the whole answer to
              "how do they get in?" and support needs it in front of them; an advocate signs in
              with a password, and a "Sign-in: —" on their page would read as something missing
              rather than something that does not apply. `last_login_at` is written on every
              sign-in by `resolve_social_user`. */}
          {!isReviewer && (
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.18em] uppercase text-[#6B7280]">Sign-in</div>
              <div className="mt-1.5" data-testid="person-signin"><SignInBadges user={u} /></div>
              <div className="text-xs text-[#6B7280] mt-1.5">
                {u.last_login_at ? `Last seen ${fmtDate(u.last_login_at)}` : "Never signed in"}
              </div>
            </div>
          )}
          <CopyField label="User ID" value={u.id} display={String(u.id || "").slice(0, 8) + "…"}
                     mono testid="copy-person-id" />
          {!isReviewer && <AddressList addresses={u.addresses} legacy={u.postal_address} />}
        </div>
      </Panel>

      {/* 2 — how much they have done with us */}
      <Panel title={isReviewer ? "Caseload" : "Activity"} testid="person-stats">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {isReviewer ? (
            <>
              <Stat label="Assigned" value={u.assigned_count ?? 0} testid="stat-assigned" />
              <Stat label="Open" value={u.open_count ?? 0} testid="stat-open" />
              <Stat label="Closed" value={u.closed_count ?? 0} testid="stat-closed" />
            </>
          ) : (
            <>
              <Stat label="Cases" value={txns.length} testid="stat-cases" />
              <Stat label="Paid" value={paid.length} testid="stat-paid" />
              <Stat label="Spend" value={`₹${spend.toLocaleString()}`} testid="stat-spend" />
            </>
          )}
        </div>
      </Panel>

      {/* 3 — the work itself */}
      <Panel title={isReviewer ? "Assigned cases" : "Transactions"} testid="person-txns-panel">
        <AdminTable
          testid="person-txns" columns={COLUMNS} gridCols={GRID} rows={txns}
          defaultSort="created"
          empty={isReviewer ? "No cases assigned to this advocate yet." : "This customer has no documents yet."}
          searchPlaceholder="Search txn, document, status…"
          matches={(t, q) => [
            t.id, t.document_name, t.template_title, t.service_type, t.subtype,
            t.status, t.payment_status, t.review_status, t.delivery_status,
          ].filter(Boolean).join(" ").toLowerCase().includes(q)}
          accessors={{
            txn: (t) => t.id,
            document: (t) => (t.document_name || t.template_title || "").toLowerCase(),
            created: (t) => new Date(t.created_at).getTime(),
            status: (t) => t.status || "",
            payment: (t) => t.payment_status || "",
            review: (t) => t.review_status || "",
            delivery: (t) => t.delivery_status || "",
          }}
          renderRow={(t) => (
            <>
              <CopyableId value={t.id} label="Txn ID" testid={`copy-txn-${t.id}`}
                          onOpen={() => nav(`/admin/txn/${t.id}`)} />
              <div className="pt-1.5 min-w-0">
                <div className="text-sm text-white truncate">
                  {t.document_name || t.template_title || t.service_type}
                </div>
                {t.subtype && (
                  <div className="text-xs text-[#6B7280] capitalize truncate">
                    {String(t.subtype).replace(/_/g, " ")}
                  </div>
                )}
              </div>
              <DateTimeCell value={t.created_at} />
              <div className="pt-1.5"><StatusPill status={t.status} /></div>
              <div className="pt-1.5"><StatusPill status={t.payment_status} /></div>
              <div className="pt-1.5"><StatusPill status={t.review_status} /></div>
              <div className="pt-1.5"><StatusPill status={t.delivery_status} /></div>
            </>
          )}
        />
      </Panel>
    </div>
  );
}

export const AdminUserDetail = () => <PersonDetail role="customer" />;
export const AdminReviewerDetail = () => <PersonDetail role="reviewer" />;
export default PersonDetail;
